import { ISalesChannel } from '@/app/containers/Admin/PricingMenuChannels/api'

export interface IChannelOption {
  id: string
  label: string
  active: boolean
}

export type ChannelLoadStatus = 'loading' | 'error' | 'ready'

/**
 * An empty channel scope means "unscoped — access to every channel" on the backend.
 * That must only ever happen because an admin deliberately chose it, never because the
 * channel list failed to load. Submission is only safe once the list has actually loaded.
 */
export const isChannelScopeSubmittable = (status: ChannelLoadStatus): boolean => status === 'ready'

const formatChannelLabel = (channel: ISalesChannel): string =>
  `${channel.name} (${channel.environment})`

export const buildChannelOptions = (channels: ISalesChannel[]): IChannelOption[] =>
  channels.map((channel) => ({
    id: channel.id,
    label: formatChannelLabel(channel),
    active: channel.active,
  }))

/** Resolves scoped channel ids to display names, falling back to the raw id when unknown. */
export const getScopedChannelNames = (
  channelIds: string[] | undefined,
  channels: ISalesChannel[],
): string[] => {
  if (!channelIds || channelIds.length === 0) {
    return []
  }

  const channelsById = new Map(channels.map((channel) => [channel.id, channel]))

  return channelIds.map((channelId) => {
    const channel = channelsById.get(channelId)
    return channel ? formatChannelLabel(channel) : channelId
  })
}

export interface ChannelScopeDisplay {
  /** True when the source itself has no channelIds — a deliberate, permanent grant to every channel. */
  isUnscoped: boolean
  /** True when the source IS scoped and the channel list has not arrived yet, so names are unresolved. */
  isResolving: boolean
  /** True when the source IS scoped but the channel list failed to load, so names may just be raw ids. */
  loadFailed: boolean
  /** Display names for a scoped source once the list is available; empty while unscoped or resolving. */
  names: string[]
}

/**
 * Decides what an external-source card should show for its channel scope.
 *
 * Three things must stay distinct, because they look identical if you only count ids:
 * genuinely unscoped (no channelIds — a deliberate, permanent choice), still resolving
 * (the list has not arrived), and failed to load. The last two leave the source scoped
 * either way; only the first is a grant.
 *
 * Names are withheld until the list is actually available. Showing them earlier means
 * showing raw uuids, which reads as data rather than as "we don't know yet".
 */
export const getChannelScopeDisplay = (
  channelIds: string[] | undefined,
  channels: ISalesChannel[],
  status: ChannelLoadStatus,
): ChannelScopeDisplay => {
  const isUnscoped = !channelIds || channelIds.length === 0
  const isResolving = !isUnscoped && status === 'loading'
  const loadFailed = !isUnscoped && status === 'error'

  return {
    isUnscoped,
    isResolving,
    loadFailed,
    names: isUnscoped || isResolving ? [] : getScopedChannelNames(channelIds, channels),
  }
}
